/**
 * Type guard that returns true if its argument is neither null nor undefined.
 *
 * Intended as a more readable version of `x != null`, as is not obvious that the non-strict equality checking will
 * check against both null and undefined, and only those values. (See
 * http://adripofjavascript.com/blog/drips/equals-equals-null-in-javascript.html)
 *
 * Can be used to perform type narrowing, e.g.:
 * ```ts
 * let foo: string | undefined;
 * // ...
 * if (isNonNullable(foo)) {
 *     // Within this branch, Typescript will understand that foo cannot be undefined and type foo as string
 *     console.log(foo.toUpperCase());  // Does not complain, as foo is just string here
 * } else {
 *     // Likewise, Typescript understands that foo can only be undefined in this branch
 * }
 * ```
 *
 * @see isNullable
 * @param x value to check
 * @return true if x is neither null nor undefined, false if x is null or undefined
 */
export const isNonNullable = <T>(x: T): x is NonNullable<T> => x != null;

/**
 * Type guard that returns true if its argument is null or undefined.
 *
 * Intended as a more readable version of `x == null`, as is not obvious that the non-strict equality checking will
 * check against both null and undefined, and only those values. (See
 * http://adripofjavascript.com/blog/drips/equals-equals-null-in-javascript.html)
 *
 * Can be used to perform type narrowing, e.g.:
 * ```ts
 * let foo: string | undefined;
 * // ...
 * if (isNullable(foo)) {
 *     // Within this branch, Typescript will understand that foo cannot be string and type foo as undefined
 * } else {
 *     // Likewise, Typescript understands that foo can only be string in this branch
 *     console.log(foo.toUpperCase());  // Does not complain, as foo is just string here
 * }
 * ```
 *
 * @see isNonNullable
 * @param x value to check
 * @return true if x is null or undefined, false if x is neither null nor undefined
 */
export const isNullable = <T>(x: T): x is T & (null | undefined) => x == null;
